import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Leaf, MapPin, Ruler, Sun, TreePine, Download, Copy, Check,
  Droplets, Calendar, Bug, Flower2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareablePlan = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans/${planId}`);
      setPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch plan:", error);
      toast.error("Failed to load garden plan");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current || !plan) return;
    
    setGenerating(true);
    toast.info("Generating PDF...");

    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#F5F5F0"
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      // Add header
      pdf.setFillColor(44, 74, 59);
      pdf.rect(0, 0, pdfWidth, 25, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text("ROOTSPACE", 10, 15);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text("Sustainable Garden Design", 10, 21);

      // Add garden name
      pdf.setTextColor(44, 74, 59);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(plan.name, 10, 35);
      
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(plan.location, 10, 42);

      // Add captured content
      const contentY = 50;
      const availableHeight = pdfHeight - contentY - 20;
      const scaledHeight = imgHeight * ratio;
      
      if (scaledHeight <= availableHeight) {
        pdf.addImage(imgData, "PNG", imgX, contentY, imgWidth * ratio, scaledHeight);
      } else {
        // Scale to fit
        const fitRatio = availableHeight / scaledHeight;
        pdf.addImage(imgData, "PNG", imgX, contentY, imgWidth * ratio * fitRatio, availableHeight);
      }

      // Add footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 10, pdfHeight - 10);
      pdf.text("Created with ROOTSPACE - permaculture.app", pdfWidth - 70, pdfHeight - 10);

      // Save PDF
      pdf.save(`${plan.name.replace(/\s+/g, "_")}_garden_plan.pdf`);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const getLayerColor = (layer) => {
    const colors = {
      canopy: "bg-emerald-800",
      understory: "bg-emerald-600",
      shrub: "bg-green-500",
      herbaceous: "bg-lime-500",
      ground_cover: "bg-lime-300",
      vine: "bg-green-400",
      root: "bg-amber-600"
    };
    return colors[layer] || "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Garden plan not found</p>
          <Link to="/">
            <Button className="btn-primary">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-7 h-7 text-primary" />
            <span className="font-serif text-xl font-bold text-primary">ROOTSPACE</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copied!" : "Copy Link"}
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={generating}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {generating ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        </div>
      </header>

      {/* Printable Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div ref={printRef} className="space-y-6 bg-background p-4">
          {/* Plan Header */}
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl font-bold text-foreground mb-2" data-testid="plan-name">
              {plan.name}
            </h1>
            <p className="text-muted-foreground flex items-center justify-center gap-1">
              <MapPin className="w-4 h-4" />
              {plan.location}
            </p>
            <Badge className="mt-2 capitalize" variant="secondary">
              {plan.aesthetic} Garden
            </Badge>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-organic">
              <CardContent className="p-4 text-center">
                <Ruler className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Dimensions</p>
                <p className="font-mono font-semibold text-sm">
                  {plan.width}m × {plan.length}m × {plan.height}m
                </p>
              </CardContent>
            </Card>
            <Card className="card-organic">
              <CardContent className="p-4 text-center">
                <TreePine className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Plants</p>
                <p className="font-mono font-semibold text-sm">
                  {plan.plant_recommendations?.length || 0} species
                </p>
              </CardContent>
            </Card>
            {plan.solar_data && (
              <>
                <Card className="card-organic">
                  <CardContent className="p-4 text-center">
                    <Sun className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    <p className="text-xs text-muted-foreground">Summer Sun</p>
                    <p className="font-mono font-semibold text-sm">
                      {plan.solar_data.sun_hours_summer}h/day
                    </p>
                  </CardContent>
                </Card>
                <Card className="card-organic">
                  <CardContent className="p-4 text-center">
                    <Sun className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                    <p className="text-xs text-muted-foreground">Winter Sun</p>
                    <p className="font-mono font-semibold text-sm">
                      {plan.solar_data.sun_hours_winter}h/day
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Design Description */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Design Philosophy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">
                {plan.ai_description || "A sustainable permaculture garden designed for optimal yield and ecosystem health."}
              </p>
            </CardContent>
          </Card>

          {/* Permaculture Principles */}
          {plan.permaculture_principles?.length > 0 && (
            <Card className="card-organic">
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-primary" />
                  Permaculture Principles Applied
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid md:grid-cols-2 gap-2">
                  {plan.permaculture_principles.map((principle, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span>{principle}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Plant List */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Flower2 className="w-5 h-5 text-secondary" />
                Plant Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {(plan.plant_recommendations || []).map((plant, i) => (
                  <div 
                    key={i}
                    className="p-3 border border-border rounded-lg bg-white/50"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-sm">{plant.name}</h4>
                        {plant.scientific_name && (
                          <p className="text-xs text-muted-foreground italic">{plant.scientific_name}</p>
                        )}
                      </div>
                      <Badge className={`${getLayerColor(plant.layer)} text-white text-xs`}>
                        {plant.layer?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Sun className="w-3 h-3" />
                        {plant.sun_requirement?.replace('_', ' ')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Droplets className="w-3 h-3" />
                        {plant.water_needs}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Plant: {plant.planting_season}
                      </span>
                    </div>
                    {plant.companion_plants?.length > 0 && (
                      <p className="text-xs mt-2">
                        <span className="text-muted-foreground">Companions: </span>
                        {plant.companion_plants.slice(0, 3).join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Seasonal Tasks */}
          {plan.seasonal_tasks && Object.keys(plan.seasonal_tasks).length > 0 && (
            <Card className="card-organic">
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Seasonal Care Guide
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {['spring', 'summer', 'fall', 'winter'].map(season => (
                    <div key={season} className="space-y-2">
                      <h4 className="font-semibold capitalize text-sm flex items-center gap-1">
                        {season === 'spring' && <Flower2 className="w-4 h-4 text-pink-500" />}
                        {season === 'summer' && <Sun className="w-4 h-4 text-amber-500" />}
                        {season === 'fall' && <Leaf className="w-4 h-4 text-orange-500" />}
                        {season === 'winter' && <Bug className="w-4 h-4 text-blue-500" />}
                        {season}
                      </h4>
                      <ul className="space-y-1">
                        {(plan.seasonal_tasks[season] || []).slice(0, 3).map((task, i) => (
                          <li key={i} className="text-xs text-muted-foreground">
                            • {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="text-center pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Created with ROOTSPACE • Sustainable Garden Design
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShareablePlan;
